<template>
    <div class="ra-root">

        <!-- Header -->
        <div class="ra-header">
            <div class="ra-header-left">
                <div class="ra-header-icon">
                    <i class="fas fa-chart-bar"></i>
                </div>
                <div>
                    <h3 class="ra-title">Visão Geral</h3>
                    <p class="ra-subtitle">{{ filtered.length }} campanha(s) encontrada(s)</p>
                </div>
            </div>
            <div class="ra-header-right">
                <div class="ra-date-range">
                    <label class="ra-date-label">De</label>
                    <input class="ra-date-input" type="date" v-model="dateStart" @change="load" />
                    <label class="ra-date-label">Até</label>
                    <input class="ra-date-input" type="date" v-model="dateEnd" @change="load" />
                    <button v-if="dateStart || dateEnd" class="ra-date-clear" @click="clearDates" title="Limpar datas">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="ra-search-wrap">
                    <i class="fas fa-search ra-search-icon"></i>
                    <input class="ra-search" type="text" placeholder="Pesquisar campanha..." v-model="search" />
                </div>
                <!-- Export buttons -->
                <button class="ra-export-btn ra-export-btn--excel" @click="exportExcel" title="Exportar Excel">
                    <i class="fas fa-file-excel"></i> Excel
                </button>
                <button class="ra-export-btn ra-export-btn--pdf" @click="exportPdf" title="Exportar PDF">
                    <i class="fas fa-file-pdf"></i> PDF
                </button>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="ra-loading">
            <i class="fas fa-spinner fa-spin"></i> Carregando dados...
        </div>

        <template v-else>

            <!-- KPI Cards -->
            <div class="ra-kpis">
                <div class="ra-kpi ra-kpi--total">
                    <div class="ra-kpi__icon"><i class="fas fa-paper-plane"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.total.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Total envios</span>
                    </div>
                </div>
                <div class="ra-kpi ra-kpi--read">
                    <div class="ra-kpi__icon"><i class="fas fa-check-double"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.lido.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Lidas</span>
                        <span class="ra-kpi__pct" v-if="totals.total">{{ pct(totals.lido, totals.total) }}%</span>
                    </div>
                </div>
                <div class="ra-kpi ra-kpi--delivered">
                    <div class="ra-kpi__icon"><i class="fas fa-check-double"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.recebido.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Entregues</span>
                        <span class="ra-kpi__pct" v-if="totals.total">{{ pct(totals.recebido, totals.total) }}%</span>
                    </div>
                </div>
                <div class="ra-kpi ra-kpi--sent">
                    <div class="ra-kpi__icon"><i class="fas fa-check"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.enviado.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Enviadas</span>
                        <span class="ra-kpi__pct" v-if="totals.total">{{ pct(totals.enviado, totals.total) }}%</span>
                    </div>
                </div>
                <div class="ra-kpi ra-kpi--pending">
                    <div class="ra-kpi__icon"><i class="fas fa-clock"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.pendente.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Pendentes</span>
                    </div>
                </div>
                <div class="ra-kpi ra-kpi--failed">
                    <div class="ra-kpi__icon"><i class="fas fa-times-circle"></i></div>
                    <div class="ra-kpi__body">
                        <span class="ra-kpi__value">{{ totals.falha.toLocaleString('pt-BR') }}</span>
                        <span class="ra-kpi__label">Falhas</span>
                    </div>
                </div>
            </div>

            <!-- Tabela -->
            <div class="ra-table-wrap">
                <table class="ra-table">
                    <thead>
                        <tr>
                            <th class="ra-th sortable" @click="sortBy('created_at')">Data <i
                                    :class="sortIcon('created_at')"></i></th>
                            <th class="ra-th sortable" @click="sortBy('nome_campanha')">Campanha <i
                                    :class="sortIcon('nome_campanha')"></i></th>
                            <th class="ra-th ra-th--center">Pendente</th>
                            <th class="ra-th ra-th--center">Enviado</th>
                            <th class="ra-th ra-th--center">Entregue</th>
                            <th class="ra-th ra-th--center">Lido</th>
                            <th class="ra-th ra-th--center">Falha</th>
                            <th class="ra-th ra-th--center">Progresso de Envio</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!filtered.length">
                            <td colspan="8" class="ra-empty">Nenhuma campanha encontrada.</td>
                        </tr>
                        <tr v-for="(row, i) in filtered" :key="i" class="ra-tr">
                            <td class="ra-td"><span class="ra-date">{{ formatDate(row.created_at) }}</span></td>
                            <td class="ra-td">
                                <div class="ra-campaign-cell">
                                    <span class="ra-campaign-dot"></span>
                                    <span class="ra-campaign">{{ row.nome_campanha }}</span>
                                </div>
                            </td>
                            <td class="ra-td ra-td--center"><span class="ra-chip ra-chip--pending">{{ row.Pendente || 0
                                    }}</span></td>
                            <td class="ra-td ra-td--center"><span class="ra-chip ra-chip--sent">{{ row.Enviado || 0
                                    }}</span></td>
                            <td class="ra-td ra-td--center"><span class="ra-chip ra-chip--delivered">{{ row.Recebido ||
                                    0 }}</span></td>
                            <td class="ra-td ra-td--center"><span class="ra-chip ra-chip--read">{{ row.Lido || 0
                                    }}</span></td>
                            <td class="ra-td ra-td--center"><span class="ra-chip ra-chip--failed">{{ row['Nao-Entregue']
                                    || 0 }}</span></td>
                            <td class="ra-td ra-td--center">
                                <div class="ra-prog">
                                    <div class="ra-prog__bar">
                                        <div class="ra-prog__fill"
                                            :style="{ width: calcPct(row) + '%', background: progColor(row) }"></div>
                                    </div>
                                    <span class="ra-prog__pct" :style="{ color: progColor(row) }">{{ calcPct(row)
                                        }}%</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </template>
    </div>
</template>

<script>
export default {
    props: { filter: { type: Array, default: function () { return []; } } },
    data() { return { rows: [], loading: false, search: '', sortKey: 'created_at', sortDir: 'desc', dateStart: '', dateEnd: '' }; },
    computed: {
        filtered() {
            var s = this.search.toLowerCase(), key = this.sortKey, dir = this.sortDir;
            var selected = (this.filter && this.filter.length) ? this.filter : null;

            var rows = this.rows;

            if (selected) {
                rows = rows.filter(function (r) { return selected.indexOf(r.nome_campanha) !== -1; });
            }

            if (s) {
                rows = rows.filter(function (r) { return (r.nome_campanha || '').toLowerCase().includes(s); });
            }

            return rows.slice().sort(function (a, b) { var av = a[key] || '', bv = b[key] || ''; return dir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1); });
        },
        totals() {
            return this.filtered.reduce(function (acc, r) {
                var t = (r.Pendente || 0) + (r.Enviado || 0) + (r.Recebido || 0) + (r.Lido || 0) + (r['Nao-Entregue'] || 0);
                acc.total += t; acc.pendente += r.Pendente || 0; acc.enviado += r.Enviado || 0;
                acc.recebido += r.Recebido || 0; acc.lido += r.Lido || 0; acc.falha += r['Nao-Entregue'] || 0;
                return acc;
            }, { total: 0, pendente: 0, enviado: 0, recebido: 0, lido: 0, falha: 0 });
        }
    },
    watch: { filter: { handler() { this.load(); }, deep: true } },
    methods: {
        async load() {
            this.loading = true;
            try {
                var params = {};

                if (this.filter && this.filter.length === 1) {
                    params.param = this.filter[0];
                } else {
                    params.param = 'todos';
                }
                if (this.dateStart) params.date_start = this.dateStart;
                if (this.dateEnd) params.date_end = this.dateEnd;
                var res = await axios.get('/marketing/analise', { params });
                this.rows = Array.isArray(res.data) ? res.data : Object.values(res.data);
            } catch (e) { this.rows = []; }
            this.loading = false;
        },
        clearDates() { this.dateStart = ''; this.dateEnd = ''; this.load(); },
        sortBy(key) { if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; else { this.sortKey = key; this.sortDir = 'asc'; } },
        sortIcon(key) { if (this.sortKey !== key) return 'fas fa-sort ra-si'; return this.sortDir === 'asc' ? 'fas fa-sort-up ra-si--on' : 'fas fa-sort-down ra-si--on'; },
        formatDate(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
        pct(v, t) { return t ? Math.round((v / t) * 100) : 0; },
        calcPct(row) {
            var t = (row.Pendente || 0) + (row.Enviado || 0) + (row.Recebido || 0) + (row.Lido || 0) + (row['Nao-Entregue'] || 0);
            return t ? Math.round(((t - (row.Pendente || 0)) / t) * 100) : 0;
        },
        progColor(row) { var p = this.calcPct(row); return p >= 80 ? '#22c55e' : p >= 40 ? '#f59e0b' : '#ef4444'; },
        progClass(row) { var p = this.calcPct(row); return p >= 80 ? 'ra-prog--ok' : p >= 40 ? 'ra-prog--warn' : 'ra-prog--danger'; },

        _exportRows() {
            var self = this;
            return this.filtered.map(function (r) {
                var t = (r.Pendente || 0) + (r.Enviado || 0) + (r.Recebido || 0) + (r.Lido || 0) + (r['Nao-Entregue'] || 0);
                var pct = t ? Math.round(((t - (r.Pendente || 0)) / t) * 100) : 0;
                return {
                    'Data': r.created_at ? moment(r.created_at).format('DD/MM/YYYY HH:mm') : '—',
                    'Campanha': r.nome_campanha || '—',
                    'Pendente': r.Pendente || 0,
                    'Enviado': r.Enviado || 0,
                    'Entregue': r.Recebido || 0,
                    'Lido': r.Lido || 0,
                    'Falha': r['Nao-Entregue'] || 0,
                    'Progresso': pct + '%'
                };
            });
        },

        exportExcel() {
            var self = this;
            function doExport() {
                var XLSX = window.XLSX;
                var data = self._exportRows();
                var ws = XLSX.utils.json_to_sheet(data);
                var wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Visão Geral');
                XLSX.writeFile(wb, 'visao_geral_' + moment().format('YYYYMMDD_HHmm') + '.xlsx');
            }
            if (window.XLSX) { doExport(); return; }
            var s = document.createElement('script');
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            s.onload = doExport;
            document.head.appendChild(s);
        },

        exportPdf() {
            var self = this;
            function doExport() {
                var doc = new window.jspdf.jsPDF({ orientation: 'landscape' });
                doc.setFontSize(13);
                doc.text('Visão Geral de Campanha', 14, 15);
                doc.setFontSize(9);
                doc.text('Gerado em ' + moment().format('DD/MM/YYYY HH:mm'), 14, 21);

                var rows = self._exportRows();
                var cols = Object.keys(rows[0] || {});
                doc.autoTable({
                    startY: 26,
                    head: [cols],
                    body: rows.map(function (r) { return cols.map(function (c) { return r[c]; }); }),
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: [0, 168, 132], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [248, 249, 250] },
                    columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 50 } }
                });
                doc.save('visao_geral_' + moment().format('YYYYMMDD_HHmm') + '.pdf');
            }

            if (window.jspdf && window.jspdf.jsPDF) { doExport(); return; }
            var s1 = document.createElement('script');
            s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            s1.onload = function () {
                var s2 = document.createElement('script');
                s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
                s2.onload = doExport;
                document.head.appendChild(s2);
            };
            document.head.appendChild(s1);
        }
    },
    mounted() { this.load(); }
};
</script>

<style scoped>
.ra-root {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.ra-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 16px;
    flex-wrap: wrap;
    gap: 12px;
}

.ra-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
}

.ra-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.ra-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px !important;
    background: linear-gradient(135deg, #00a884, #00c49a);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ra-header-icon i {
    font-size: 18px;
    color: #fff;
}

.ra-title {
    font-size: 16px;
    font-weight: 700;
    color: #111b21;
    margin: 0;
    line-height: 1.2;
}

.ra-subtitle {
    font-size: 12px;
    color: #adb5bd;
    margin: 0;
    margin-top: 2px;
}

.ra-date-range {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f8f9fa;
    border: 1.5px solid #e9edef;
    border-radius: 8px !important;
    padding: 0 10px;
    height: 36px;
}

.ra-date-label {
    font-size: 11px;
    font-weight: 600;
    color: #adb5bd;
    white-space: nowrap;
}

.ra-date-input {
    height: 28px;
    padding: 0 4px;
    border: none;
    background: transparent;
    font-size: 13px;
    color: #111b21;
    outline: none;
    width: 130px;
}

.ra-date-input:focus {
    color: #00a884;
}

.ra-date-clear {
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 50% !important;
    background: #fecdd3;
    color: #be123c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;
}

.ra-date-clear:hover {
    background: #fca5a5;
}

.ra-search-wrap {
    position: relative;
}

.ra-search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #adb5bd;
    font-size: 12px;
    pointer-events: none;
}

.ra-search {
    height: 36px;
    padding: 0 12px 0 32px;
    border: 1.5px solid #e9edef;
    border-radius: 8px !important;
    font-size: 13px;
    color: #111b21;
    outline: none;
    width: 220px;
    transition: border-color .15s;
}

.ra-search:focus {
    border-color: #00a884;
}

.ra-export-btn {
    height: 36px;
    padding: 0 12px;
    border: 1.5px solid;
    border-radius: 8px !important;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background .15s;
    white-space: nowrap;
}

.ra-export-btn--excel {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #15803d;
}

.ra-export-btn--excel:hover {
    background: #dcfce7;
}

.ra-export-btn--pdf {
    background: #fff1f2;
    border-color: #fecdd3;
    color: #be123c;
}

.ra-export-btn--pdf:hover {
    background: #ffe4e6;
}

.ra-loading {
    padding: 48px;
    text-align: center;
    font-size: 14px;
    color: #adb5bd;
}

.ra-loading i {
    margin-right: 6px;
    color: #00a884;
}

.ra-kpis {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding: 0 24px 20px;
}

.ra-kpi {
    flex: 1;
    min-width: 110px;
    border-radius: 12px !important;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid transparent;
}

.ra-kpi__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ra-kpi__icon i {
    font-size: 15px;
}

.ra-kpi__body {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.ra-kpi__value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
}

.ra-kpi__label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    margin-top: 2px;
}

.ra-kpi__pct {
    font-size: 11px;
    font-weight: 500;
    opacity: .7;
}

.ra-kpi--total {
    background: #f8f9fa;
    border-color: #e9edef;
}

.ra-kpi--total .ra-kpi__icon {
    background: #e9ecef;
}

.ra-kpi--total .ra-kpi__icon i {
    color: #495057;
}

.ra-kpi--total .ra-kpi__value {
    color: #111b21;
}

.ra-kpi--total .ra-kpi__label {
    color: #667781;
}

.ra-kpi--read {
    background: #f0fdf4;
    border-color: #bbf7d0;
}

.ra-kpi--read .ra-kpi__icon {
    background: #bbf7d0;
}

.ra-kpi--read .ra-kpi__icon i {
    color: #15803d;
}

.ra-kpi--read .ra-kpi__value,
.ra-kpi--read .ra-kpi__label,
.ra-kpi--read .ra-kpi__pct {
    color: #15803d;
}

.ra-kpi--delivered {
    background: #eff6ff;
    border-color: #bfdbfe;
}

.ra-kpi--delivered .ra-kpi__icon {
    background: #bfdbfe;
}

.ra-kpi--delivered .ra-kpi__icon i {
    color: #1d4ed8;
}

.ra-kpi--delivered .ra-kpi__value,
.ra-kpi--delivered .ra-kpi__label,
.ra-kpi--delivered .ra-kpi__pct {
    color: #1d4ed8;
}

.ra-kpi--sent {
    background: #f8fafc;
    border-color: #e2e8f0;
}

.ra-kpi--sent .ra-kpi__icon {
    background: #e2e8f0;
}

.ra-kpi--sent .ra-kpi__icon i {
    color: #475569;
}

.ra-kpi--sent .ra-kpi__value,
.ra-kpi--sent .ra-kpi__label {
    color: #475569;
}

.ra-kpi--pending {
    background: #fffbeb;
    border-color: #fde68a;
}

.ra-kpi--pending .ra-kpi__icon {
    background: #fde68a;
}

.ra-kpi--pending .ra-kpi__icon i {
    color: #b45309;
}

.ra-kpi--pending .ra-kpi__value,
.ra-kpi--pending .ra-kpi__label {
    color: #b45309;
}

.ra-kpi--failed {
    background: #fff1f2;
    border-color: #fecdd3;
}

.ra-kpi--failed .ra-kpi__icon {
    background: #fecdd3;
}

.ra-kpi--failed .ra-kpi__icon i {
    color: #be123c;
}

.ra-kpi--failed .ra-kpi__value,
.ra-kpi--failed .ra-kpi__label {
    color: #be123c;
}

.ra-table-wrap {
    overflow-x: auto;
    border-top: 1px solid #f0f2f5;
}

.ra-table {
    width: 100%;
    border-collapse: collapse;
}

.ra-th {
    background: #fafbfc;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    padding: 10px 16px;
    border-bottom: 1px solid #f0f2f5;
    text-align: left;
    white-space: nowrap;
}

.ra-th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background .1s;
}

.ra-th.sortable:hover {
    background: #f0f2f5;
}

.ra-th--center {
    text-align: center;
}

.ra-tr {
    border-bottom: 1px solid #f8f9fa;
    transition: background .1s;
}

.ra-tr:hover {
    background: #f8fffe;
}

.ra-td {
    padding: 12px 16px;
    font-size: 13px;
    color: #343a40;
    vertical-align: middle;
}

.ra-td--center {
    text-align: center;
}

.ra-empty {
    padding: 48px;
    text-align: center;
    font-size: 13px;
    color: #adb5bd;
}

.ra-date {
    font-size: 12px;
    color: #94a3b8;
}

.ra-campaign-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ra-campaign-dot {
    width: 7px;
    height: 7px;
    border-radius: 50% !important;
    background: #00a884;
    flex-shrink: 0;
}

.ra-campaign {
    font-weight: 600;
    color: #111b21;
    font-size: 13px;
}

.ra-chip {
    display: inline-block;
    min-width: 28px;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 99px !important;
}

.ra-chip--pending {
    background: #fef9c3;
    color: #a16207;
}

.ra-chip--sent {
    background: #f1f5f9;
    color: #475569;
}

.ra-chip--delivered {
    background: #dbeafe;
    color: #1d4ed8;
}

.ra-chip--read {
    background: #dcfce7;
    color: #15803d;
}

.ra-chip--failed {
    background: #ffe4e6;
    color: #be123c;
}

.ra-prog {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 80px;
}

.ra-prog__bar {
    width: 100%;
    height: 5px;
    background: #f0f2f5;
    border-radius: 99px !important;
    overflow: hidden;
}

.ra-prog__fill {
    height: 100%;
    border-radius: 99px !important;
    transition: width .3s;
}

.ra-prog__pct {
    font-size: 11px;
    font-weight: 700;
}

.ra-si {
    color: #d1d5db;
    font-size: 10px;
    margin-left: 3px;
}

.ra-si--on {
    color: #00a884;
    font-size: 10px;
    margin-left: 3px;
}
</style>